import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, FileText, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface ContentBrief {
  id: string;
  topic: string;
  target_keywords: string[];
  content_outline: any;
  status: string;
  seo_score: number | null;
  created_at: string;
}

export const ContentOptimization = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [existingContent, setExistingContent] = useState('');
  const [briefs, setBriefs] = useState<ContentBrief[]>([]);
  const [generatedBrief, setGeneratedBrief] = useState('');

  useEffect(() => {
    fetchBriefs();
  }, []);

  const fetchBriefs = async () => {
    const { data, error } = await supabase
      .from('seo_content_briefs')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching briefs:', error);
    } else {
      setBriefs(data || []);
    }
  };

  const handleOptimize = async () => {
    if (!topic || !keywords) {
      toast({
        title: 'Missing Information',
        description: 'Please provide topic and target keywords',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('seo-automation', {
        body: {
          action: 'content_optimization',
          data: {
            topic,
            targetKeywords: keywords.split(',').map(k => k.trim()),
            existingContent: existingContent || null
          }
        }
      });

      if (error) throw error;

      setGeneratedBrief(data.content_brief);
      toast({
        title: 'Optimization Complete',
        description: 'Content brief generated successfully',
      });

      await fetchBriefs();
      setTopic('');
      setKeywords('');
      setExistingContent('');
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to optimize content',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Content Creation & Optimization
          </CardTitle>
          <CardDescription>
            Generate SEO-optimized content briefs and optimize existing content
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="topic">Content Topic</Label>
            <Input
              id="topic"
              placeholder="e.g., Benefits of smart toilets"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="keywords">Target Keywords (comma-separated)</Label>
            <Input
              id="keywords"
              placeholder="smart toilet, luxury bathroom, hygiene"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="existing">Existing Content (optional)</Label>
            <Textarea
              id="existing"
              placeholder="Paste existing content to optimize..."
              value={existingContent}
              onChange={(e) => setExistingContent(e.target.value)}
              rows={6}
            />
          </div>

          <Button onClick={handleOptimize} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Content Brief...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Content Brief
              </>
            )}
          </Button>

          {generatedBrief && (
            <Card className="mt-4 bg-muted">
              <CardHeader>
                <CardTitle className="text-lg">Generated Content Brief</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="whitespace-pre-wrap text-sm">{generatedBrief}</div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Content Briefs History ({briefs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {briefs.map((brief) => (
              <Card key={brief.id}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <h4 className="font-semibold">{brief.topic}</h4>
                      <div className="flex gap-2 flex-wrap">
                        {brief.target_keywords.map((kw, idx) => (
                          <Badge key={idx} variant="secondary">{kw}</Badge>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {new Date(brief.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant={brief.status === 'draft' ? 'outline' : 'default'}>
                      {brief.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {briefs.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No content briefs yet. Generate one above.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};